import { useState, useEffect, useContext } from "react";
import {
   Checkbox,
   Row,
   Col,
   Button,
   message,
   Form,
   Input,
   Divider,
   Collapse,
   Result,
} from "antd";
import { useGroupRoleActions } from "@iso/actions/groupRole.actions";
import { useParams } from "react-router-dom";
import { SaveOutlined } from "@ant-design/icons";
import styled from "styled-components";
import Page from "component/Page/index";
import Loading from "../Loading/index";
import useAuthor from "hooks/useAuthor";
import { useOidc, useOidcUser } from "@iso/helpers/oidc";
import { UserContext } from "../../context/UserContext";

const { Panel } = Collapse;

const buildTree = (permissions) => {
   if (!permissions) return [];
   const permissionMap = {};
   const roots = [];

   permissions.forEach((p) => {
      permissionMap[p.name] = { ...p, children: [] };
   });

   permissions.forEach((p) => {
      if (p.parentName && permissionMap[p.parentName]) {
         permissionMap[p.parentName].children.push(permissionMap[p.name]);
      } else {
         roots.push(permissionMap[p.name]);
      }
   });

   return roots;
};

const CustomStyle = styled.div`
   .ant-checkbox-wrapper {
      margin-bottom: 8px;
      white-space: nowrap;
   }
   .ant-collapse {
      width: 100%;
      margin-bottom: 16px;
   }
   .ant-collapse-header {
      align-items: center !important;
      h2 {
         margin-bottom: 0;
      }
   }
   h3 {
      width: 100%;
   }
`;

const GroupRoleDetail = () => {
   const { id } = useParams();
   const [form] = Form.useForm();
   const [checkedListDict, setCheckedListDict] = useState({});
   const [data, setData] = useState();
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const groupRoleActions = useGroupRoleActions();
   const {
      user,
      reloadRoles,
      isLoading: isUserLoading,
   } = useContext(UserContext);

   const getData = async () => {
      setIsLoading(true);
      try {
         const role = await groupRoleActions.getRoleById(id);
         const resGet = await groupRoleActions.getRoleDetail(id);
         form.setFieldsValue({
            name: role?.name || "-",
         });
         if (resGet?.groups) {
            const _checkedListDict = { ...checkedListDict };
            resGet?.groups.forEach((group) => {
               group.permissions.forEach((permission) => {
                  _checkedListDict[permission.name] = permission.isGranted;
               });
            });
            setCheckedListDict(_checkedListDict);
         }
         setData(resGet);
      } catch (error) {
         console.log(error);
      } finally {
         setIsLoading(false);
      }
   };

   const onRoleClick = (node, checked) => {
      let _checkedListDict = { ...checkedListDict };

      const updateNodeAndChildren = (curNode) => {
         _checkedListDict[curNode.name] = checked;
         if (curNode.children) {
            curNode.children.forEach(updateNodeAndChildren);
         }
      };

      updateNodeAndChildren(node);

      if (checked) {
         let currentParentName = node.parentName;
         while (currentParentName) {
            _checkedListDict[currentParentName] = true;
            let foundParent = null;
            if (data?.groups) {
               for (const group of data.groups) {
                  const p = group.permissions.find(
                     (x) => x.name === currentParentName
                  );
                  if (p) {
                     foundParent = p;
                     break;
                  }
               }
            }
            currentParentName = foundParent ? foundParent.parentName : null;
         }
      }

      setCheckedListDict(_checkedListDict);
   };

   const isNodeAllChecked = (node) => {
      if (!node.children || node.children.length === 0)
         return !!checkedListDict[node.name];
      return (
         !!checkedListDict[node.name] && node.children.every(isNodeAllChecked)
      );
   };

   const isNodeIndeterminate = (node) => {
      if (!node.children || node.children.length === 0) return false;
      const isAnyChildCheckedOrIndeterminate = (n) => {
         return (
            !!checkedListDict[n.name] ||
            (n.children && n.children.some(isAnyChildCheckedOrIndeterminate))
         );
      };
      const hasCheckedDescendant = node.children.some(
         isAnyChildCheckedOrIndeterminate
      );
      return (
         (!!checkedListDict[node.name] || hasCheckedDescendant) &&
         !isNodeAllChecked(node)
      );
   };

   const renderPermissionNode = (node, depth = 0) => {
      return (
         <div
            key={node.name}
            style={{
               marginLeft: depth > 0 ? 24 : 0,
               marginBottom: 8,
               display: "flex",
               flexDirection: "column",
            }}
         >
            <Checkbox
               checked={isNodeAllChecked(node)}
               indeterminate={isNodeIndeterminate(node)}
               value={node.name}
               onChange={(e) => onRoleClick(node, e.target.checked)}
            >
               <h4
                  style={{
                     margin: 0,
                     display: "inline-block",
                     fontWeight: depth === 0 ? 600 : 400,
                  }}
               >
                  {node.displayName?.toString()?.replaceAll("_", " ")}
               </h4>
            </Checkbox>
            {node.children && node.children.length > 0 && (
               <div style={{ marginTop: 8 }}>
                  {node.children.map((child) =>
                     renderPermissionNode(child, depth + 1)
                  )}
               </div>
            )}
         </div>
      );
   };

   const onCheckAllChange = (e) => {
      const checked = e.target.checked;
      let _checkedListDict = { ...checkedListDict };
      data?.groups?.forEach((group) => {
         group.permissions.forEach((permission) => {
            _checkedListDict[permission.name] = checked;
         });
      });
      setCheckedListDict(_checkedListDict);
   };

   const isAllChecked =
      data?.groups?.length > 0 &&
      data?.groups?.every((group) =>
         group.permissions.every(
            (permission) => checkedListDict[permission.name]
         )
      );

   const isIndeterminate =
      data?.groups?.some((group) =>
         group.permissions.some(
            (permission) => checkedListDict[permission.name]
         )
      ) && !isAllChecked;

   const onCheckGroupChange = (e, group) => {
      console.log("group:", e.target.checked);
      const checked = e.target.checked;
      let _checkedListDict = { ...checkedListDict };
      group.permissions.forEach((permission) => {
         _checkedListDict[permission.name] = checked;
      });
      setCheckedListDict(_checkedListDict);
   };

   const isGroupAllChecked = (group) => {
      if (!group?.permissions?.length) return false;
      return group.permissions.every(
         (permission) => !!checkedListDict[permission.name]
      );
   };

   const isGroupIndeterminate = (group) => {
      if (!group?.permissions?.length) return false;
      const someChecked = group.permissions.some(
         (permission) => !!checkedListDict[permission.name]
      );
      return someChecked && !isGroupAllChecked(group);
   };

   const onSubmit = async () => {
      setIsSubmitting(true);
      try {
         await groupRoleActions.updateRoleById(id, form.getFieldsValue());
         await groupRoleActions.updateGroupRole(id, {
            permissions: Object.entries(checkedListDict).map(
               ([name, isGranted]) => ({
                  name,
                  isGranted,
               })
            ),
         });
         message.success("Update Roles success!");
      } catch (error) {
         console.log(error);
      } finally {
         setIsSubmitting(false);
         await reloadRoles();
      }
   };

   useEffect(() => {
      getData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <>
         {isLoading || isUserLoading || !user?.permissions ? (
            <Loading />
         ) : user &&
           user.permissions?.["BackOfficeMenu.IAM.Roles.AssignPermission"] ? (
            <Page title="Role Detail">
               <div className="assign-store-wrapper">
                  <div className="bg-white-0 rounded-sm shadow-sm">
                     <div className="w-2/5 flex mb-8 pt-8 px-6">
                        <div className="flex-grow mr-4">
                           <Form onFinish={onSubmit} form={form}>
                              <div className="flex">
                                 <div className="flex-grow">
                                    <Form.Item
                                       name="name"
                                       label="Name"
                                       rules={[
                                          {
                                             required: true,
                                             message: "Name is required!",
                                          },
                                       ]}
                                    >
                                       <Input placeholder="Name" />
                                    </Form.Item>
                                 </div>
                              </div>
                           </Form>
                        </div>
                     </div>
                  </div>
                  <hr />
                  <div className="flex items-center justify-between my-7">
                     <h2 className="m-0" style={{ margin: 0 }}>
                        Roles list
                     </h2>
                     <Checkbox
                        onChange={onCheckAllChange}
                        checked={isAllChecked}
                        indeterminate={isIndeterminate}
                     >
                        Check all
                     </Checkbox>
                  </div>
                  <CustomStyle>
                     <Row>
                        <Collapse>
                           {data?.groups
                              ? data?.groups.map((group, i) => (
                                   <Panel
                                      header={
                                         <div
                                            className="flex items-center justify-between w-full"
                                            style={{
                                               width: "100%",
                                               paddingRight: "16px",
                                            }}
                                         >
                                            <h2
                                               className="m-0"
                                               style={{ margin: 0 }}
                                            >
                                               {group.displayName}
                                            </h2>
                                            <div
                                               onClick={(e) =>
                                                  e.stopPropagation()
                                               }
                                            >
                                               <Checkbox
                                                  onChange={(e) => {
                                                     e.stopPropagation();
                                                     onCheckGroupChange(
                                                        e,
                                                        group
                                                     );
                                                  }}
                                                  checked={isGroupAllChecked(
                                                     group
                                                  )}
                                                  indeterminate={isGroupIndeterminate(
                                                     group
                                                  )}
                                               ></Checkbox>
                                            </div>
                                         </div>
                                      }
                                      key={i}
                                   >
                                      <Row gutter={[16, 16]}>
                                         {buildTree(group.permissions).map(
                                            (rootNode, index) => (
                                               <Col span={8} key={index}>
                                                  {renderPermissionNode(
                                                     rootNode
                                                  )}
                                               </Col>
                                            )
                                         )}
                                      </Row>
                                   </Panel>
                                ))
                              : ""}
                        </Collapse>
                     </Row>
                  </CustomStyle>
                  {/*{checkRoles(["group_role_edit"]) && (*/}
                  <div className="px-6 py-8 mb-12 bg-white-0 rounded-sm shadow-sm text-right">
                     <Button
                        loading={isSubmitting}
                        type="primary"
                        onClick={onSubmit}
                     >
                        <p className="flex items-center">
                           <SaveOutlined />
                           <span className="ml-2">Save</span>
                        </p>
                     </Button>
                  </div>
                  {/*)}*/}
               </div>
            </Page>
         ) : (
            <Result
               status="403"
               title="403"
               subTitle="Sorry, you don't have allowance to access this page"
               extra={<Button type="primary">Back Home</Button>}
            />
         )}
      </>
   );
};

export default GroupRoleDetail;
